import { Link } from "react-router-dom";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

// Composant dynamique : mentions légales alimentées par /api/legal-identity
// Si incomplet → page NON-PUBLIÉE (pas de placeholder trompeur en public).
function LegalIdentityBlock({ onIncomplete }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/legal-identity").then((r) => {
      setData(r.data);
      if (!r.data?.complete && onIncomplete) onIncomplete();
    }).catch(() => setData({ complete: false, missing_fields: [] }));
  }, [onIncomplete]);
  if (!data) return <p className="text-[#5C6B7A]">Chargement…</p>;
  if (!data.complete) {
    return (
      <div className="p-8 bg-white border-2 border-[#E5E7EB] rounded-2xl text-center" data-testid="legal-identity-not-published">
        <div className="text-4xl mb-3">📄</div>
        <h2 className="font-heading font-bold text-xl text-[#2C3E50] mb-2">Page en cours de préparation</h2>
        <p className="text-sm text-[#5C6B7A] mb-4 max-w-lg mx-auto">
          Les mentions légales officielles de Déménageur Pro sont en cours de rédaction et seront publiées
          prochainement, une fois les informations juridiques exactes de l'entité exploitante intégrées.
        </p>
        <p className="text-xs text-[#5C6B7A]">
          Pour toute question dans l'intervalle, contactez-nous :{" "}
          <a href="mailto:demenageurpro.contact@gmail.com" className="text-[#1AA66C] font-semibold hover:underline">
            demenageurpro.contact@gmail.com
          </a>
        </p>
      </div>
    );
  }
  const rows = [
    ["Dénomination sociale", data.denomination_sociale],
    ["Nom commercial", data.nom_commercial || "Déménageur Pro"],
    ["Forme juridique", data.forme_juridique],
    ["Capital social", data.capital_social],
    ["Adresse du siège", data.adresse_siege],
    ["SIREN", data.siren],
    ["SIRET", data.siret],
    ["RCS", data.rcs_ville],
    ["N° TVA intracommunautaire", data.tva_intracom],
    ["Représentant légal", data.representant_legal],
    ["Directeur de la publication", data.directeur_publication],
    ["Téléphone", data.telephone],
    ["Email", data.email],
    ["Hébergeur", `${data.hebergeur_raison_sociale} — ${data.hebergeur_adresse}`],
    ["Téléphone hébergeur", data.hebergeur_telephone],
  ].filter(([, v]) => v);
  return (
    <div>
      <h2 className="font-heading font-extrabold text-2xl mb-3 text-[#2C3E50]">Identité juridique de l'exploitant</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm" data-testid="legal-identity-complete">
        {rows.map(([k, v]) => (
          <div key={k}><span className="text-[#5C6B7A]">{k} : </span><span className="font-semibold text-[#2C3E50]">{v}</span></div>
        ))}
      </div>
    </div>
  );
}

const PAGES = {
  "mentions-legales": {
    title: "Mentions légales",
    eyebrow: "Légal",
    sections: [
      { h: "Éditeur du site", p: "Déménageur Pro — Plateforme française d'annuaire des entreprises de déménagement. Hébergement et données stockées dans l'Union Européenne." },
      { h: "Responsable de publication", p: "Le directeur de la publication peut être contacté à l'adresse : demenageurpro.contact@gmail.com" },
      { h: "Hébergement", p: "Le site est hébergé chez un prestataire situé dans l'Union Européenne, conformément au RGPD." },
      { h: "Propriété intellectuelle", p: "L'ensemble des contenus (textes, marques, logos) présents sur ce site sont la propriété de leurs auteurs respectifs et sont protégés par le droit français et international de la propriété intellectuelle." },
      { h: "Contact", p: "Pour toute question relative au site ou à vos données, contactez-nous à : demenageurpro.contact@gmail.com" },
    ],
  },
  cgu: {
    title: "Conditions générales d'utilisation",
    eyebrow: "CGU / CGV",
    sections: [
      { h: "Objet", p: "Les présentes CGU régissent l'utilisation du site Déménageur Pro. En utilisant le site, vous acceptez ces conditions." },
      { h: "Service", p: "Le site met gratuitement à disposition un annuaire d'entreprises de déménagement et un service de mise en relation via un formulaire de demande de devis." },
      { h: "Annuaire des entreprises", p: "Les fiches entreprises présentes sur Déménageur Pro peuvent être créées à partir de données professionnelles accessibles publiquement (INSEE Sirene, Pappers, site officiel de l'entreprise). Ces fiches ont une finalité informative. La présence d'une entreprise dans l'annuaire ne signifie pas automatiquement qu'elle est partenaire de Déménageur Pro. Une distinction claire est affichée sur chaque fiche entre les entreprises \"partenaires\" (ayant accepté un partenariat) et les entreprises \"référencées\" (listées à titre informatif). Toute entreprise peut demander la modification ou la suppression de sa fiche via le formulaire dédié ou en écrivant à demenageurpro.contact@gmail.com." },
      { h: "Demande de devis", p: "Lorsque vous soumettez une demande, vos coordonnées sont transmises uniquement à des entreprises partenaires ou expressément autorisées à recevoir ces demandes, afin qu'elles puissent vous proposer un devis. Les demandes ne sont jamais transmises aux entreprises simplement référencées à titre informatif dans l'annuaire. Les devis sont gratuits et sans engagement." },
      { h: "Responsabilité", p: "L'éditeur n'est pas partie aux relations contractuelles entre les utilisateurs et les entreprises de déménagement. Nous nous efforçons d'assurer l'exactitude des informations publiées sans garantie d'exhaustivité." },
      { h: "Modification", p: "Les présentes CGU peuvent être modifiées à tout moment. La version applicable est celle en vigueur au moment de l'utilisation du site." },
    ],
  },
  confidentialite: {
    title: "Politique de confidentialité",
    eyebrow: "RGPD",
    sections: [
      { h: "Données collectées", p: "Lors d'une demande de devis : nom, prénom, email, téléphone, adresses de départ et d'arrivée, volume et date du déménagement, message libre. Ces données sont indispensables au traitement de votre demande." },
      { h: "Finalité du traitement", p: "Vos données sont utilisées exclusivement pour transmettre votre demande aux entreprises de déménagement partenaires afin qu'elles puissent vous contacter avec une proposition." },
      { h: "Base légale", p: "Le traitement repose sur votre consentement explicite (case à cocher du formulaire) et sur l'exécution de mesures précontractuelles." },
      { h: "Destinataires", p: "Les entreprises de déménagement partenaires sélectionnées en fonction de la zone géographique de votre déménagement. Les entreprises simplement référencées à titre informatif dans notre annuaire ne reçoivent jamais vos coordonnées." },
      { h: "Durée de conservation", p: "Les données sont conservées 3 ans à compter de votre dernière interaction avec le service." },
      { h: "Vos droits", p: "Vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité, d'opposition et de limitation. Pour exercer ces droits : demenageurpro.contact@gmail.com" },
      { h: "Réclamation", p: "Vous pouvez introduire une réclamation auprès de la CNIL (www.cnil.fr) si vous estimez que vos droits ne sont pas respectés." },
    ],
  },
  cookies: {
    title: "Cookies & traceurs",
    eyebrow: "RGPD",
    sections: [
      { h: "Qu'est-ce qu'un cookie ?", p: "Un cookie est un petit fichier déposé sur votre appareil. Il sert à faciliter votre navigation ou à mesurer l'audience du site." },
      { h: "Cookies utilisés", p: "Nous utilisons uniquement des cookies strictement nécessaires au fonctionnement du site (notamment pour l'authentification administrateur). Aucun cookie publicitaire n'est déposé sans votre consentement." },
      { h: "Gestion", p: "Vous pouvez à tout moment configurer votre navigateur pour bloquer ou supprimer les cookies. Le fonctionnement du site peut alors être altéré." },
    ],
  },
  "a-propos": {
    title: "Comment ça marche",
    eyebrow: "À propos",
    sections: [
      { h: "Notre mission", p: "Faciliter le déménagement des Français en simplifiant la recherche et la mise en relation avec les meilleures entreprises de déménagement, partout en France." },
      { h: "Comment ça marche ?", p: "Notre annuaire référence des centaines d'entreprises de déménagement à partir d'informations professionnelles publiques. Vous remplissez un formulaire unique, nous transmettons votre demande à nos entreprises partenaires qui vous contacteront pour vous proposer un devis." },
      { h: "Pourquoi nous choisir ?", p: "100% gratuit pour les particuliers · Annuaire national · Devis sans engagement · Plateforme française · Données hébergées en UE · Distinction claire entre entreprises partenaires et entreprises référencées à titre informatif." },
      { h: "Vous êtes une entreprise ?", p: "Vous représentez une entreprise référencée dans notre annuaire ? Vous pouvez à tout moment demander la modification ou la suppression de votre fiche via notre formulaire dédié, ou nous contacter pour devenir partenaire : demenageurpro.contact@gmail.com" },
    ],
  },
  "mentions-legales-b2b": {
    title: "Mentions légales B2B — utilisation des données professionnelles",
    eyebrow: "Prospection B2B",
    sections: [
      { h: "Nature des données publiées", p: "Notre annuaire ne référence que des personnes morales (entreprises immatriculées) et diffuse exclusivement des informations professionnelles publiques : dénomination sociale, adresse du siège ou de l'établissement, numéro de téléphone professionnel, site internet, et lorsque disponible, numéro SIRET et forme juridique. Aucune donnée personnelle relative à un individu (nom d'un gérant à titre personnel, téléphone privé, e-mail nominatif) n'est publiée." },
      { h: "Sources utilisées", p: "Les informations proviennent de sources ouvertes et officielles : base Sirene de l'INSEE (data.gouv.fr), registre Pappers, sites internet publiés par les entreprises elles-mêmes, et annuaires professionnels publics. Nous ne procédons à aucun scraping automatisé de Google Maps ni ne recopions les avis clients rédigés par des tiers." },
      { h: "Finalité — prospection B2B", p: "Ces informations sont diffusées à des fins d'information du grand public (mise en relation avec des professionnels du déménagement) et de prospection commerciale entre professionnels (B2B). Le traitement repose sur l'intérêt légitime, conformément à l'article 6-1-f du RGPD et aux lignes directrices de la CNIL relatives à la prospection B2B." },
      { h: "Pas d'avis clients recopiés", p: "Nous ne stockons ni ne diffusons de textes d'avis clients issus de plateformes tierces (Google, Trustpilot, etc.). Les entreprises restent seules maîtresses de leur e-réputation sur les plateformes concernées." },
      { h: "Droit d'opposition et de suppression", p: "Toute entreprise référencée peut demander à tout moment la rectification ou la suppression de sa fiche en écrivant à demenageurpro.contact@gmail.com. Nous nous engageons à traiter ces demandes sous 7 jours ouvrés, sans justification requise." },
      { h: "Absence de démarchage abusif", p: "Aucune fiche référencée ne fait l'objet de démarchage commercial non sollicité à partir des données publiées. Les demandes de devis émises par les utilisateurs finaux ne sont transmises qu'aux entreprises consentantes ou géographiquement pertinentes." },
      { h: "Contact — données B2B", p: "Pour toute demande relative à la fiche de votre entreprise (correction, retrait, opposition) : demenageurpro.contact@gmail.com" },
    ],
  },
};

export default function Legal({ pageKey }) {
  const page = PAGES[pageKey] || PAGES["mentions-legales"];
  const [legalIncomplete, setLegalIncomplete] = useState(false);
  useEffect(() => {
    if (pageKey === "mentions-legales" && legalIncomplete) {
      let el = document.querySelector('meta[name="robots"]');
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", "robots"); document.head.appendChild(el); }
      el.setAttribute("content", "noindex,nofollow");
    }
  }, [pageKey, legalIncomplete]);
  return (
    <div className="animate-fade-up py-16 md:py-24" data-testid={`legal-page-${pageKey}`}>
      <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
        <div className="eyebrow mb-4">{page.eyebrow}</div>
        <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight text-[#2C3E50] mb-12">{page.title}</h1>
        <div className="space-y-10">
          {pageKey === "mentions-legales" && (
            <LegalIdentityBlock onIncomplete={() => setLegalIncomplete(true)} />
          )}
          {/* Sections statiques masquées tant que mentions légales incomplètes (Section 2 briefing 2026-02-15) */}
          {!(pageKey === "mentions-legales" && legalIncomplete) && page.sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-heading font-extrabold text-2xl mb-3 text-[#2C3E50]">{s.h}</h2>
              <p className="text-[#6B7280] leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
        <div className="mt-16 pt-8 border-t border-[#E5E7EB]">
          <Link to="/" className="btn-outline"><ArrowLeft className="w-4 h-4" /> Retour à l'accueil</Link>
        </div>
      </div>
    </div>
  );
}
